import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import mappingData from "../../mapping.json";
import { Accordion, Button, Col, Form, Row } from "react-bootstrap";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import ProductMapping from "./ProductMapping";
import { saveProductMapping } from "../../store/slices/saveProductMapping";
import { getConfig } from '../../store/slices/getConfig';
import * as Yup from 'yup'; // Import Yup for validation
import CustomToast from "../common/CustomToast";

function ProductDataMapping({ showToast }) {
  const dispatch = useDispatch();
  const savedValues = useSelector((state) => state.config.data);
  // const relationship = useSelector((state) => state.associationOptions.options);
  const filterOption = useSelector((state) => state.PImAttributes.newOption);
  const configId = useSelector((state) => state.saveCreds.configId);
  const defaultOption = useSelector((state) => state.PImAttributes.display);
  const salesChannelInfo = useSelector(state=>state.salesChannel.optionsData);
  const relatedProductsoptionsData = useSelector(state => state.associationOptions.options)

  // Ref to track the form element for scrolling
  const formRef = useRef(null);

  //TOAST STATE
  const [toastStatus, setToastStatus] = useState('');
  const [toastBody, setToastBody] = useState('');

  // Categorize mapping data
  const {
    productOrganization,
    publishingSettings,
    media,
    pricing,
    inventory,
    shipping,
    variantMapping,
    metafields,
    relationships,
    searchEngineListing,
    importImages,
    relatedproduct

  } = useMemo(() => {
    const categories = {
      productOrganization: [],
      publishingSettings: [],
      media: [],
      pricing: [],
      inventory: [],
      shipping: [],
      variantMapping: [],
      metafields: [],
      relationships: [],
      searchEngineListing: [],
      importImages: [],
      relatedproduct: []
    };

    mappingData.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (categories[key] !== undefined) {
          categories[key].push(...item[key]);
        }
      });
    });

    return categories;
  }, []);

  const [defaultValues, setDefaultValues] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const options = useMemo(
    () => ({
      true: "Yes",
      false: "No",
    }),
    []
  );

  const salesDefaultValue = useMemo(()=>{
      const item = salesChannelInfo?.find((value)=>value.name === "Online Store");
      return item?.id?.replace("gid://shopify/Publication/","");
  }
  ,[salesChannelInfo])

  // This loop ensures that ANY field marked as "required" in mapping JSON
  const validationSchema = useMemo(() => {
    const shape = {};
    mappingData.forEach((group) => {
      Object.values(group).forEach((items) => {
        items.forEach((item) => {
          if (item.componentType === "required") {
            shape[item.code] = Yup.lazy((val) => {
              if (typeof val === 'object' && val !== null) {
                return Yup.object().shape({
                  value: Yup.string().required(`${item.title} is required`)
                }).required(`${item.title} is required`);
              }
              return Yup.string().required(`${item.title} is required`);
            });
          }
        });
      });
    });
    return Yup.object().shape(shape);
  }, []);

  // Memoized function to set initial values
  const getInitialValues = useCallback(() => {
    const newMappingObj = {};

    const settingInitialValues = (MappingObj, MappingName) => {
      if (!MappingObj) return;

      MappingObj.forEach((item) => {
        let mappingValue = savedValues?.attributeMapping?.[MappingName]?.[item.code];

        if (!mappingValue) {
          mappingValue = savedValues?.attributeMapping?.[item.code];
        }

        if (!mappingValue && MappingName === "variantMapping") {
          const cleanCode = item.code.replace("variant_", "");
          mappingValue = savedValues?.attributeMapping?.[MappingName]?.[cleanCode];
        }

        if(MappingName === "searchEngineListing"){
          if(item.code=="seotitle"){
            mappingValue = savedValues?.attributeMapping?.[MappingName]?.["seo.title"];
          }
          else if(item.code=="seodescription"){
            mappingValue = savedValues?.attributeMapping?.[MappingName]?.["seo.description"];
          }
          else if(item.code == "handle"){
            mappingValue = savedValues?.attributeMapping?.["productOrganization"]?.["handle"];
          }
        }

        if(MappingName === "relatedproduct"){
          if(item.code=="relatedproduct"){
            mappingValue = savedValues?.attributeMapping?.relatedProduct?.associationType;
          }
        }

        if (item.field_type === "simple") {
          if (item.code === "variantImage") {
            const varinatImageVal = savedValues?.attributeMapping?.[item.code] || mappingValue;
            if (varinatImageVal?.pimAttribute) {
              newMappingObj[item.code] = {
                value: varinatImageVal.pimAttribute,
                label: defaultOption?.[varinatImageVal.pimAttribute] || varinatImageVal.pimAttribute,
                pimType: varinatImageVal?.pimType
              };
            } else if (varinatImageVal?.clientDefaultValue) {
              newMappingObj[item.code] = varinatImageVal.clientDefaultValue;
            } else {
              newMappingObj[item.code] = "";
            }
          } else if (item.code === "importImages") {
            if (mappingValue !== undefined && mappingValue !== "") {
              newMappingObj[item.code] = {
                value: mappingValue.toString(),
                label: options[mappingValue.toString()],
              };
            }
          }else if(item.code === "relatedproduct"){
            if(mappingValue){
            newMappingObj[item.code] = {
                value: mappingValue,
                label: relatedProductsoptionsData?.[mappingValue] || savedValues?.attributeMapping?.relatedProduct?.metafield_pattern?.defName,
              };
            }
          } else {
            if (mappingValue?.pimAttribute) {
              newMappingObj[item.code] = {
                value: mappingValue.pimAttribute,
                label: defaultOption?.[mappingValue.pimAttribute] || mappingValue.pimAttribute,
                pimType: mappingValue?.pimType
              };
            } else if (mappingValue?.clientDefaultValue) {
              newMappingObj[item.code] = mappingValue.clientDefaultValue;
            } else {
              newMappingObj[item.code] = "";
            }
          }
        } else {
          if (Array.isArray(mappingValue) && mappingValue.length > 0) {
            newMappingObj[item.code] = mappingValue.map((val) => ({
              value: val.pimAttribute,
              label: defaultOption?.[val.pimAttribute] || val.pimAttribute,
              pimType: val?.pimType
            }));
          } else if (mappingValue?.pimAttribute) {
            newMappingObj[item.code] = [{
              value: mappingValue.pimAttribute,
              label: defaultOption?.[mappingValue.pimAttribute] || mappingValue.pimAttribute,
              pimType: mappingValue?.pimType
            }];
          }
        }
      });
    };

    settingInitialValues(productOrganization, "productOrganization");
    settingInitialValues(publishingSettings, "publishing Settings");
    settingInitialValues(media, "media");
    settingInitialValues(pricing, "pricing");
    settingInitialValues(inventory, "inventory");
    settingInitialValues(shipping, "shipping");
    settingInitialValues(variantMapping, "variantMapping");
    settingInitialValues(metafields, "metafields");
    settingInitialValues(relationships, "relationships");
    settingInitialValues(searchEngineListing, "searchEngineListing");
    settingInitialValues(importImages, "importImages");
    settingInitialValues(relatedproduct,"relatedproduct")

    return newMappingObj;
  }, [savedValues, defaultOption, options, productOrganization, publishingSettings, media, pricing, inventory, shipping, variantMapping, metafields, relationships, searchEngineListing, importImages]);

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: validationSchema, // Keeps form synced with JSON requirements
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      // Clear any validation toasts
      // console.log(values)
      setToastStatus('');
      
      try {
        const createArrayMapping = (mappingObj, newValuesArray, mappingName) => {
          if (!mappingObj) return;
          Object.values(mappingObj).forEach((item) => {
            let code;
            if (mappingName === "variantMapping") {
              // code = item.code.split("_")[1];
              code = item.code.replace("variant_", "");
            } else if(item.code === "seotitle"){
              code = "seo.title"
            }else if(item.code === "seodescription"){
              code = "seo.description"
            }else{
              code = item.code;
            }

            const shopifyDefaultValue = item.shopifyDefaultValue || "";
            const formValue = values[item.code];
            if(item.code == "relatedproduct"){
              if(formValue?.value){
               newValuesArray[code] = {
                      "associationType": formValue?.value || "",
                      "metafield_pattern":{
                          "key": formValue?.value || "",
                          "namespace": "productAcc|variantAcc",
                          "type": "list.product_reference",
                          "defName": formValue?.label || ""
                      }		
              }
            }
            }
            else if (item.field_type === "simple") {
              const defaultValue = defaultValues.includes(item.code) ? formValue : "";
              const pimAttribute = formValue?.value || "";
              const defaultType = formValue?.pimType || "";

              newValuesArray[code] = {
                shopifyType: item.shopifyType,
                pimAttribute: defaultValue ? "" : pimAttribute,
                clientDefaultValue: defaultValue,
                pimType: filterOption?.[pimAttribute] || defaultType,
                shopifyDefaultValue: defaultValue ? "" : item.code === "salesChannels" ? salesDefaultValue : shopifyDefaultValue,
              };
            } else if (Array.isArray(formValue)) {
              newValuesArray[code] = formValue.map((itemValue) => ({
                shopifyType: item.shopifyType,
                pimAttribute: itemValue.value,
                pimType: filterOption?.[itemValue.value] || itemValue.pimType,
                shopifyDefaultValue: shopifyDefaultValue,
              }));
            } else if (formValue && typeof formValue === "object" && formValue.value) {
              newValuesArray[code] = [{
                shopifyType: item.shopifyType,
                pimAttribute: formValue.value,
                pimType: filterOption?.[formValue.value] || itemValue.pimType,
                shopifyDefaultValue: shopifyDefaultValue,
              }];
            }
          });
        };

        const productOrganizationMapping = {};
        const publishingSettingsMapping = {};
        const mediaMapping = {};
        const pricingMapping = {};
        const inventoryMapping = {};
        const shippingMapping = {};
        const VariantMapping = {};
        const metafieldsMapping = {};
        const relationshipsMapping = {};
        const searchEngineListingMapping = {};
        const relatedProductsMapping = {}

        createArrayMapping(productOrganization, productOrganizationMapping, "productOrganization");
        createArrayMapping(publishingSettings, publishingSettingsMapping, "publishingSettings");
        createArrayMapping(media, mediaMapping, "media");
        createArrayMapping(pricing, pricingMapping, "pricing");
        createArrayMapping(inventory, inventoryMapping, "inventory");
        createArrayMapping(shipping, shippingMapping, "shipping");
        createArrayMapping(variantMapping, VariantMapping, "variantMapping");
        createArrayMapping(metafields, metafieldsMapping, "metafields");
        createArrayMapping(relationships, relationshipsMapping, "relationships");
        createArrayMapping(searchEngineListing, searchEngineListingMapping, "searchEngineListing");
        createArrayMapping(relatedproduct,relatedProductsMapping, "relatedproduct" )

        const formatSingleValue = (key) => {
          const val = values[key];
          if (val && val.value) {
            return {
              shopifyType: "String", // Default assumption, safe for most
              pimAttribute: val.value,
              clientDefaultValue: "",
              pimType: filterOption?.[val.value] || val.pimType,
              shopifyDefaultValue: "",
            };
          }
          return {}; // Return empty object if missing
        };

        const formatArrayValue = (key) => {
          const val = values[key] || values[key.replace(/([A-Z])/g, "_$1").toLowerCase()];
          if (Array.isArray(val)) {
            return val.map((v) => ({
              shopifyType: "String",
              pimAttribute: v.value,
              pimType: filterOption?.[v.value] || v.pimType,
              shopifyDefaultValue: "",
            }));
          }
          return [];
        };

        const updatedProductOrganization = {...productOrganizationMapping, "handle":searchEngineListingMapping?.["handle"]}

        const collectedValues = {
          // productOrganization: Object.fromEntries(
          //   Object.entries(productOrganizationMapping).filter(
          //     ([key]) => key !== "tags" && key !== "salesChannels"
          //   )
          // ),
          productOrganization: updatedProductOrganization || [],
          productImages: mediaMapping.productImages || [],
          variantImage: mediaMapping.variantImage || {},
          videos: mediaMapping.videos || [],
          pricing: pricingMapping,
          inventory: inventoryMapping,
          shipping: shippingMapping,
          variantMapping: VariantMapping,
          productMetaFields: formatArrayValue("productMetaFields") || [],
          variantMetaFields: formatArrayValue("variantMetaFields") || [],
          searchEngineListing: Object.fromEntries(
            Object.entries(searchEngineListingMapping).filter(
              ([key]) => key !== "handle"
            )
          ),
          associationType: relationshipsMapping.associationType?.pimAttribute || "",
          altTextForImage: mediaMapping.altTextForImage || formatSingleValue("altTextForImage"),
          salesChannels: publishingSettingsMapping.salesChannels || formatSingleValue("salesChannels"),
          importImages: values?.importImages?.value === 'true' ? true : false,
          isPreExistingCatalog: savedValues?.attributeMapping?.isPreExistingCatalog ? savedValues?.attributeMapping?.isPreExistingCatalog : "false",
          isPreExistingCategoriesCompleted: savedValues?.attributeMapping?.isPreExistingCategoriesCompleted ? savedValues?.attributeMapping?.isPreExistingCategoriesCompleted : "" ,
          isPreExistingProductCompleted: savedValues?.attributeMapping?.isPreExistingProductCompleted ? savedValues?.attributeMapping?.isPreExistingProductCompleted : "",
          relatedProduct: relatedProductsMapping.relatedproduct || {},
          assetCollection: savedValues?.attributeMapping?.assetCollection || {},
          markets:savedValues?.attributeMapping?.markets || {},
          includeCatalogAttribute:{"pimAttribute":values?.includeCatalogAttribute?.value || "", "pimType":values?.includeCatalogAttribute?.pimType || ""},
          excludeCatalogAttribute:{"pimAttribute":values?.excludeCatalogAttribute?.value || "", "pimType":values?.excludeCatalogAttribute?.pimType || ""}
        };

        const submitResponse = {
          attributeMapping: collectedValues,
          struConfigId: configId || savedValues?.struConfigId,
        };

        await dispatch(saveProductMapping(submitResponse)).unwrap();
        await dispatch(getConfig(savedValues?.targetClientDomain)).unwrap();
        
        if(showToast) {
          showToast("saveattributeMapping"); 
        }

      } catch (error) {
        console.error("Submission error:", error);
      } finally {
        setSubmitting(false); // This is crucial to re-enable the button
      }
    },
  });

  // This automatically finds the field with an error and scrolls to it.
  useEffect(() => {
    if (formik.isSubmitting && !formik.isValid) {
      const firstErrorKey = Object.keys(formik.errors)[0];
      
      // TRIGGER CUSTOM TOAST
      setToastBody("Please fill in the required fields.");
      setToastStatus("error");

      if (firstErrorKey) {
        const errorElement = document.getElementsByName(firstErrorKey)[0];

        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorElement.focus();
        } else {
          // Fallback for custom components
          const fallbackElement = document.getElementById(firstErrorKey);
          if (fallbackElement) {
            fallbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    }
  }, [formik.isSubmitting, formik.isValid, formik.errors]);

  // Initialize form values only once
  useEffect(() => {
    if (savedValues && !isInitialized) {
      formik.setValues(getInitialValues());
      setIsInitialized(true);
    }
  }, [savedValues, isInitialized, getInitialValues, formik]);

  //  Updated handleDefault to MERGE values instead of overwrite
  const handleDefault = useCallback((def_value, productValues) => {
    setDefaultValues((prevDefaultValues) => {
      const currentGroupCodes = productValues.map((item) => item.code);
      
      const otherGroupDefaults = prevDefaultValues.filter(
        (code) => !currentGroupCodes.includes(code)
      );
      
      const newGroupDefaults = productValues
        .filter((item) => def_value[item.code])
        .map((item) => item.code);

      return [...otherGroupDefaults, ...newGroupDefaults];
    });
  }, []);

  return (
    <>
      <CustomToast body={toastBody} status={toastStatus} setStatus={setToastStatus} />

      <Form onSubmit={formik.handleSubmit} ref={formRef}>
        <Accordion.Item eventKey="4" className="my-1 mt-4">
          <Accordion.Header>Product Mappings</Accordion.Header>
          <Accordion.Body className="p-4">
            {mappingData.map((group, index) => {
              const key = Object.keys(group)[0];
              const items = group[key];

              return (
                <ProductMapping
                  key={`${key}-${index}`}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  values={formik.values}
                  touched={formik.touched}
                  errors={formik.errors}
                  handleDefault={handleDefault}
                  setFieldValue={formik.setFieldValue}
                  setFieldTouched={formik.setFieldTouched}
                  productValues={items}
                  mappingName={key}
                  showToast={showToast}
                />
              );
            })}
            <Row className="mt-5 mx-3">
              <Col sm={12}>
                <Button
                  variant="primary"
                  type="submit"
                  className="stru-btn"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? 'Saving...' : 'Save Config'}
                </Button>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>
      </Form>
    </>
  );
}

export default ProductDataMapping;
